import { z } from 'zod';
import type { TaskType } from './planning';

/** Rodzaje dokumentów dołączanych do sprawy. */
export const CASE_DOCUMENT_CATEGORIES = ['projekt', 'dokumentacja', 'zdjecia', 'korespondencja', 'dotacja', 'inne'] as const;
export type CaseDocumentCategory = (typeof CASE_DOCUMENT_CATEGORIES)[number];

export const CASE_DOCUMENT_CATEGORY_LABELS: Record<CaseDocumentCategory, string> = {
  projekt: 'Projekt',
  dokumentacja: 'Dokumentacja techniczna',
  zdjecia: 'Zdjęcia',
  korespondencja: 'Korespondencja',
  dotacja: 'Dotacja',
  inne: 'Inne',
};

/**
 * Dozwolone pliki dokumentów — typ ustalany po rozszerzeniu (nie ufamy typowi z przeglądarki).
 * Bez HTML/SVG/skryptów; w przeglądarce otwieramy tylko PDF i zdjęcia, resztę pobieramy.
 */
export const DOCUMENT_TYPES: Record<string, { mime: string; inline: boolean }> = {
  pdf: { mime: 'application/pdf', inline: true },
  jpg: { mime: 'image/jpeg', inline: true },
  jpeg: { mime: 'image/jpeg', inline: true },
  png: { mime: 'image/png', inline: true },
  webp: { mime: 'image/webp', inline: true },
  heic: { mime: 'image/heic', inline: false },
  doc: { mime: 'application/msword', inline: false },
  docx: { mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', inline: false },
  xls: { mime: 'application/vnd.ms-excel', inline: false },
  xlsx: { mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', inline: false },
  odt: { mime: 'application/vnd.oasis.opendocument.text', inline: false },
  ods: { mime: 'application/vnd.oasis.opendocument.spreadsheet', inline: false },
  dwg: { mime: 'image/vnd.dwg', inline: false },
  dxf: { mime: 'image/vnd.dxf', inline: false },
  zip: { mime: 'application/zip', inline: false },
  txt: { mime: 'text/plain', inline: false },
  csv: { mime: 'text/csv', inline: false },
  eml: { mime: 'message/rfc822', inline: false },
};
export const DOCUMENT_MAX_BYTES = 50 * 1024 * 1024;
export const DOCUMENT_ACCEPT = Object.keys(DOCUMENT_TYPES)
  .map((e) => `.${e}`)
  .join(',');

export const documentExtension = (fileName: string) => (fileName.toLowerCase().match(/\.([a-z0-9]+)$/)?.[1] ?? '');

export const caseDocumentMetaSchema = z.object({
  category: z.enum(CASE_DOCUMENT_CATEGORIES).default('inne'),
  description: z.string().trim().max(500).default(''),
});
export type CaseDocumentMetaInput = z.infer<typeof caseDocumentMetaSchema>;

export interface CaseDocumentDto {
  id: string;
  caseId: string;
  category: CaseDocumentCategory;
  description: string;
  fileName: string;
  mime: string;
  size: number;
  /** można otworzyć w przeglądarce (PDF, zdjęcia) */
  inline: boolean;
  createdAt: string;
  createdByName: string | null;
  canDelete: boolean;
}

/** Zdjęcie z pracy w terenie (wizja, pomiary, montaż, poprawka, serwis…) widoczne w Dokumentach sprawy — ze źródłem. */
export interface CaseFieldPhotoDto {
  id: string;
  taskId: string;
  taskNumber: string;
  taskType: TaskType;
  taskTitle: string;
  /** termin zadania (od) */
  taskDate: string | null;
  /** czynność z listy, do której zrobiono zdjęcie (null — zdjęcie ogólne) */
  checklistLabel: string | null;
  authorName: string;
  takenAt: string;
  size: number;
  /** plik dotarł z telefonu na serwer */
  uploaded: boolean;
}
